<template>
  <s-modal
    v-model="showModal"
    class="secret-question-modal"
    :title="$t('my-data.secret-question-modal.change-secret-question')"
    :ok-title="$t('general.save')"
    :ok-disabled="invalidData"
    :is-loading="loading"
    @ok="save"
    @show="show"
    @cancel="closeModal"
  >
    <div class="secret-question-modal__tips text-green">
      {{ $t('my-data.secret-question-modal.tips') }}
    </div>
    <div class="secret-question-modal__body">
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="password">
          {{ $t('my-data.secret-question-modal.password') }}
        </label>
        <b-input
          v-model="password"
          class="secret-question-modal__password-field"
          id="password"
          maxlength="20"
          type="password"
          :state="$bsStateValidator($v.password.$error)"
          @blur.native="$v.password.$touch()"
        />
        <b-tooltip
          :show="showPasswordTooltip()"
          target="password"
          triggers=""
          placement="right"
        >
          {{
            $t('messages.required.message', {
              field: $t('my-data.secret-question-modal.password'),
            })
          }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="question">
          {{ $t('my-data.secret-question-modal.question') }}
        </label>
        <b-input
          v-model="question"
          class="text-uppercase"
          id="question"
          maxlength="150"
          :state="$bsStateValidator($v.question.$error)"
          @blur.native="$v.question.$touch()"
        />
        <b-tooltip
          :show="showQuestionTooltip()"
          target="question"
          triggers=""
          placement="right"
        >
          {{
            $t('messages.required.message', {
              field: $t('my-data.secret-question-modal.question'),
            })
          }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__field-group" role="group">
        <label class="secret-question-modal__field-label" for="answer">
          {{ $t('my-data.secret-question-modal.answer') }}
        </label>
        <b-input
          v-model="answer"
          class="text-uppercase"
          id="answer"
          maxlength="100"
          :state="$bsStateValidator($v.answer.$error)"
          @blur.native="$v.answer.$touch()"
        />
        <b-tooltip
          :show="showAnswerTooltip()"
          target="answer"
          triggers=""
          placement="right"
        >
          {{
            $t('messages.required.message', {
              field: $t('my-data.secret-question-modal.answer'),
            })
          }}
        </b-tooltip>
      </div>
      <div class="secret-question-modal__captcha" role="group">
        <captcha v-if="showCaptcha" ref="captcha" />
      </div>
    </div>
  </s-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Captcha from '@/components/captcha/Captcha';
import commonCaptchaService from '@/services/security/common';
import UserService from '@/domain/services/user';

export default {
  props: {
    value: {
      type: Boolean,
    },
  },
  components: {
    Captcha,
  },
  data() {
    return {
      password: null,
      question: null,
      answer: null,
      showCaptcha: false,
      loading: false,
    };
  },
  validations: {
    password: {
      required,
    },
    question: {
      required,
    },
    answer: {
      required,
    },
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
  },
  methods: {
    clear() {
      this.password = null;
      this.question = null;
      this.answer = null;
      this.showCaptcha = false;
      this.$v.$reset();
    },
    show() {
      this.clear();
    },
    validateCaptcha() {
      const { captcha } = this.$refs;
      captcha
        .validateCaptcha()
        .then(() => {
          this.doServiceSave();
        })
        .catch(() => {
          captcha.getCaptcha();
        });
    },
    save() {
      if (this.showCaptcha) {
        this.validateCaptcha();
        return;
      }
      this.doServiceSave();
    },
    showCaptchaHandler() {
      if (this.showCaptcha) {
        this.$refs.captcha.getCaptcha();
      }
      this.showCaptcha = true;
    },
    handleServiceSaveError(error) {
      const errorData = commonCaptchaService.extractErrorInformation(error);

      if (
        commonCaptchaService.shouldShowCaptcha(
          errorData.blockedUser,
          errorData.wrongAttempts,
        )
      ) {
        this.showCaptchaHandler();
        return;
      }
      this.showCaptcha = false;
    },
    doServiceSave() {
      this.loading = true;

      UserService.changeSecretQuestion(
        this.password,
        this.question.toUpperCase(),
        this.answer.toUpperCase(),
      )
        .then(() => {
          this.showModal = false;
          this.$pushAlert({
            code: 'MS001',
          });
        })
        .catch((error) => {
          this.handleServiceSaveError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeModal() {
      this.showModal = false;
    },
    showPasswordTooltip() {
      return this.$v.password.$error && !this.$v.password.required;
    },
    showQuestionTooltip() {
      return this.$v.question.$error && !this.$v.question.required;
    },
    showAnswerTooltip() {
      return this.$v.answer.$error && !this.$v.answer.required;
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.secret-question-modal
  &__tips
    font-style: italic
  &__field-group
    margin-bottom: 20px
    &:first-child
      margin-top: 20px
  &__field-label
    font-weight: 600
  &__body
    display: flex
    flex-direction: column
    align-items: center
    > *
      width: 70%
  &__captcha
    display: flex
    flex-direction: column
    align-items: center
    width: 100%
    margin-bottom: 20px
  +media-min-sm()
  +media-min-md()
  +media-min-lg()
</style>
